import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { validatePassword, ALLOWED_SPECIAL_CHARS } from '../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  showProgress?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showProgress = true,
}) => {
  const validation = validatePassword(password);
  const { criteria } = validation;

  // Calculer le pourcentage de force
  const criteriaCount = Object.values(criteria).filter(Boolean).length;
  const strength = (criteriaCount / 5) * 100;

  // Déterminer la couleur selon la force
  const getColor = () => {
    if (strength < 40) return 'error';
    if (strength < 80) return 'warning';
    return 'success';
  };

  // Déterminer le label de force
  const getStrengthLabel = () => {
    if (strength < 40) return 'Faible';
    if (strength < 80) return 'Moyen';
    return 'Fort';
  };

  const criteriaItems = [
    { key: 'minLength', label: 'Au moins 8 caractères', met: criteria.minLength },
    { key: 'hasUppercase', label: 'Au moins une majuscule (A-Z)', met: criteria.hasUppercase },
    { key: 'hasLowercase', label: 'Au moins une minuscule (a-z)', met: criteria.hasLowercase },
    { key: 'hasNumber', label: 'Au moins un chiffre (0-9)', met: criteria.hasNumber },
    {
      key: 'hasSpecialChar',
      label: `Au moins un caractère spécial (${ALLOWED_SPECIAL_CHARS})`,
      met: criteria.hasSpecialChar,
    },
  ];

  // Ne rien afficher si le champ est vide
  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {showProgress && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Force du mot de passe
            </Typography>
            <Typography variant="caption" color={`${getColor()}.main`} fontWeight="bold">
              {getStrengthLabel()}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={strength}
            color={getColor()}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      <Box sx={{ mt: 1 }}>
        {criteriaItems.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5,
            }}
          >
            {item.met ? (
              <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography
              variant="caption"
              color={item.met ? 'success.main' : 'text.secondary'}
              sx={{ fontSize: '0.75rem' }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
